import * as ExcelJS from 'exceljs';
import { Investor } from '@/types/investor';
import { normalizeInvestorProducts } from '@/utils/investor/productUtils';
import { formatUrl, getOptionalNumberError, isValidUrl, parseOptionalNumber } from '@/utils/investor/validation';

// Template data structure for import
export interface ImportInvestorData {
  investor: string;
  productType?: string; // Comma-separated values
  aeName?: string;
  aePhone?: string;
  aeEmail?: string;
  website?: string;
  status: 'Approved' | 'Pending' | 'Inactive';
  approvedAsOfDate?: string; // YYYY-MM-DD format
  canGoCorrespondent: string; // 'Yes' or 'No'
  correspondentStatus?: 'Approved' | 'Pending' | 'Inactive';
  correspondentApprovedAsOfDate?: string; // YYYY-MM-DD format
  delegatedType?: 'Delegated' | 'Non-Delegated' | 'Delegated/Non-Delegated';
  netWorthDelegated?: number;
  netWorthNonDelegated?: number;
  adminUsername?: string;
  notes?: string;
}

// Generate a template Excel file for download
export const generateImportTemplate = async () => {
  const templateData: ImportInvestorData[] = [
    {
      investor: 'Example Investor Corp',
      productType: 'Non-QM, Conforming, Jumbo',
      aeName: 'John Smith',
      aePhone: '555-123-4567',
      aeEmail: 'john.smith@example.com',
      website: 'https://example.com',
      status: 'Approved',
      approvedAsOfDate: '2024-01-15',
      canGoCorrespondent: 'Yes',
      correspondentStatus: 'Approved',
      correspondentApprovedAsOfDate: '2024-02-01',
      delegatedType: 'Delegated',
      netWorthDelegated: 2500000,
      netWorthNonDelegated: 0,
      adminUsername: 'example_user',
      notes: 'Sample notes for this investor'
    },
    {
      investor: 'Sample Lending LLC',
      productType: '',
      aeName: '',
      aePhone: '',
      aeEmail: '',
      website: 'https://sample.com',
      status: 'Pending',
      approvedAsOfDate: '',
      canGoCorrespondent: 'No',
      correspondentStatus: 'Inactive',
      correspondentApprovedAsOfDate: '',
      delegatedType: '',
      netWorthDelegated: undefined,
      netWorthNonDelegated: undefined,
      adminUsername: '',
      notes: ''
    }
  ];

  // Create workbook with instructions and template
  const workbook = new ExcelJS.Workbook();

  // Instructions sheet
  const instructionsSheet = workbook.addWorksheet('Instructions');
  const instructions = [
    ['Investor Import Template - Instructions'],
    [''],
    ['Required Fields:'],
    ['• investor: Company name'],
    ['• website: Company website URL'],
    ['• status: Approved, Pending, or Inactive'],
    ['• approvedAsOfDate: Date in YYYY-MM-DD format (required if status is Approved)'],
    ['• canGoCorrespondent: Yes or No'],
    [''],
    ['Optional Fields:'],
    ['• productType: Comma-separated list (Conforming, FHA, VA, Non-QM, Jumbo, USDA, DSCR, Reverse, 2nd Mortgage, HELOC, ITIN, Foreign National, Construction, Commercial, 5-8 Units)'],
    ['• aeName: Account Executive name'],
    ['• aePhone: Phone number'],
    ['• aeEmail: Email address'],
    ['• correspondentStatus: Approved, Pending, or Inactive (if canGoCorrespondent is Yes)'],
    ['• correspondentApprovedAsOfDate: Date in YYYY-MM-DD format (required if correspondentStatus is Approved)'],
    ['• delegatedType: Delegated, Non-Delegated, or Delegated/Non-Delegated'],
    ['• netWorthDelegated: Dollar amount (number only)'],
    ['• netWorthNonDelegated: Dollar amount (number only)'],
    ['• adminUsername: System username'],
    ['• notes: Additional notes'],
    [''],
    ['Notes:'],
    ['• Remove the example rows before importing'],
    ['• Dates must be in YYYY-MM-DD format'],
    ['• Product types should be comma-separated without extra spaces'],
    ['• Phone numbers can include dashes and parentheses'],
    ['• Net worth values should be numbers only (no $ or commas)']
  ];
  instructionsSheet.addRows(instructions);

  // Template sheet
  const templateSheet = workbook.addWorksheet('Import Template');
  const headers = Object.keys(templateData[0]) as (keyof ImportInvestorData)[];
  templateSheet.addRow(headers);
  templateData.forEach(row => {
    templateSheet.addRow(headers.map(header => row[header]));
  });

  // Generate and download file
  const filename = `investor_import_template_${new Date().toISOString().split('T')[0]}.xlsx`;
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Parse uploaded file and convert to Investor objects
export const parseImportFile = async (file: File): Promise<{ investors: Investor[], errors: string[] }> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);

        const worksheet = workbook.worksheets[0];
        if (!worksheet) {
          resolve({
            investors: [],
            errors: ['No sheets found in Excel file']
          });
          return;
        }

        const jsonData: ImportInvestorData[] = [];
        const headers: string[] = [];

        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) {
            // First row is headers
            row.eachCell((cell) => {
              headers.push(String(cell.value || ''));
            });
          } else {
            // Data rows
            const rowData: Record<string, unknown> = {};
            row.eachCell((cell, colNumber) => {
              const header = headers[colNumber - 1];
              rowData[header] = cell.value;
            });
            jsonData.push(rowData as unknown as ImportInvestorData);
          }
        });

        const investors: Investor[] = [];
        const errors: string[] = [];
        const cellText = (value: unknown): string => value === null || value === undefined ? '' : String(value).trim();
        // ExcelJS returns a JS Date for date-typed cells (UTC-anchored), which
        // would fail the YYYY-MM-DD regex if stringified directly.
        const normalizeDateCell = (value: unknown): string => {
          if (value instanceof Date) {
            return isNaN(value.getTime()) ? String(value) : value.toISOString().slice(0, 10);
          }
          return cellText(value);
        };
        jsonData.forEach((row, index) => {
          const rowNumber = index + 2; // +2 because Excel rows start at 1 and we have headers
          const rowErrors: string[] = [];
          const approvedDate = normalizeDateCell(row.approvedAsOfDate);
          const correspondentApprovedDate = normalizeDateCell(row.correspondentApprovedAsOfDate);

          // Validate required fields
          if (!row.investor?.toString().trim()) {
rowErrors.push(`Row ${rowNumber}: Investor name is required`);
}
          const website = formatUrl(cellText(row.website));
          if (!website) {
rowErrors.push(`Row ${rowNumber}: Website is required`);
} else if (!isValidUrl(website)) {
rowErrors.push(`Row ${rowNumber}: Website must be a valid URL`);
}
          if (!row.status) {
rowErrors.push(`Row ${rowNumber}: Status is required`);
}
          if (!row.canGoCorrespondent) {
rowErrors.push(`Row ${rowNumber}: Can Go Correspondent is required`);
}

          // Validate status values
          if (row.status && !['Approved', 'Pending', 'Inactive'].includes(row.status)) {
            rowErrors.push(`Row ${rowNumber}: Status must be 'Approved', 'Pending', or 'Inactive'`);
          }

          // Validate canGoCorrespondent
          if (row.canGoCorrespondent && !['Yes', 'No'].includes(row.canGoCorrespondent)) {
            rowErrors.push(`Row ${rowNumber}: Can Go Correspondent must be 'Yes' or 'No'`);
          }

          // Validate email format
          if (row.aeEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(row.aeEmail))) {
            rowErrors.push(`Row ${rowNumber}: Invalid email format`);
          }

          // Validate date formats
          if (approvedDate && !/^\d{4}-\d{2}-\d{2}$/.test(approvedDate)) {
            rowErrors.push(`Row ${rowNumber}: Approved date must be in YYYY-MM-DD format`);
          }

          if (correspondentApprovedDate && !/^\d{4}-\d{2}-\d{2}$/.test(correspondentApprovedDate)) {
            rowErrors.push(`Row ${rowNumber}: Correspondent approved date must be in YYYY-MM-DD format`);
          }

          if (row.status === 'Approved' && !approvedDate) {
            rowErrors.push(`Row ${rowNumber}: Approved date is required when status is Approved`);
          }

          if (row.canGoCorrespondent === 'Yes' && !row.correspondentStatus) {
            rowErrors.push(`Row ${rowNumber}: Correspondent status is required when Can Go Correspondent is Yes`);
          }

          if (row.canGoCorrespondent === 'Yes' && row.correspondentStatus && !['Approved', 'Pending', 'Inactive'].includes(row.correspondentStatus)) {
            rowErrors.push(`Row ${rowNumber}: Correspondent status must be 'Approved', 'Pending', or 'Inactive'`);
          }

          if (row.canGoCorrespondent === 'Yes' && row.correspondentStatus === 'Approved' && !correspondentApprovedDate) {
            rowErrors.push(`Row ${rowNumber}: Correspondent approved date is required when correspondent status is Approved`);
          }

          const delegatedNetWorthError = getOptionalNumberError(row.netWorthDelegated, 'Delegated net worth', { min: 0 });
          if (delegatedNetWorthError) {
            rowErrors.push(`Row ${rowNumber}: ${delegatedNetWorthError}`);
          }

          const nonDelegatedNetWorthError = getOptionalNumberError(row.netWorthNonDelegated, 'Non-delegated net worth', { min: 0 });
          if (nonDelegatedNetWorthError) {
            rowErrors.push(`Row ${rowNumber}: ${nonDelegatedNetWorthError}`);
          }

          const rawProductTypes = cellText(row.productType)
            .split(',')
            .map(p => p.trim())
            .filter(p => p);
          const productTypes = normalizeInvestorProducts(rawProductTypes);
          if (rawProductTypes.length > 0 && productTypes.length === 0) {
            rowErrors.push(`Row ${rowNumber}: Product type must use a supported investor product`);
          }

          if (rowErrors.length > 0) {
            errors.push(...rowErrors);
            return;
          }

          // Convert to Investor object
          const canGoCorrespondent = row.canGoCorrespondent === 'Yes';
          const delegatedNetWorth = parseOptionalNumber(row.netWorthDelegated);
          const nonDelegatedNetWorth = parseOptionalNumber(row.netWorthNonDelegated);
          const netWorthRequirements = {
            ...(delegatedNetWorth !== undefined ? { delegated: delegatedNetWorth } : {}),
            ...(nonDelegatedNetWorth !== undefined ? { nonDelegated: nonDelegatedNetWorth } : {})
          };

          const investor: Investor = {
            id: `import_${Date.now()}_${index}`,
            investor: String(row.investor).trim(),
            productType: productTypes,
            aeName: cellText(row.aeName),
            aePhone: cellText(row.aePhone),
            aeEmail: cellText(row.aeEmail),
            website,
            status: row.status,
            approvedAsOfDate: approvedDate || undefined,
            canDoWholesale: true,
            wholesaleProducts: productTypes,
            wholesaleAeName: cellText(row.aeName),
            wholesaleAePhone: cellText(row.aePhone),
            wholesaleAeEmail: cellText(row.aeEmail),
            wholesaleWebsite: '',
            canGoCorrespondent,
            correspondentStatus: canGoCorrespondent ? (row.correspondentStatus || 'Inactive') : 'Inactive',
            correspondentApprovedAsOfDate: canGoCorrespondent && correspondentApprovedDate
              ? correspondentApprovedDate
              : undefined,
            correspondentProducts: canGoCorrespondent ? productTypes : [],
            // Item 6: map the legacy single delegatedType import value into the new product-level
            // delegation shape (Delegated/Non-Delegated -> all correspondent products; mixed/blank -> empty).
            delegation: {
              delegated: canGoCorrespondent && row.delegatedType === 'Delegated' ? [...productTypes] : [],
              nonDelegated: canGoCorrespondent && row.delegatedType === 'Non-Delegated' ? [...productTypes] : []
            },
            correspondentAeName: canGoCorrespondent ? cellText(row.aeName) : '',
            correspondentAePhone: canGoCorrespondent ? cellText(row.aePhone) : '',
            correspondentAeEmail: canGoCorrespondent ? cellText(row.aeEmail) : '',
            correspondentWebsite: '',
            netWorthRequirements: canGoCorrespondent ? netWorthRequirements : {},
            adminUsername: cellText(row.adminUsername),
            notes: row.notes?.toString().trim() || '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            history: [{
              id: `history_${Date.now()}`,
              action: 'Created via bulk import',
              details: `Imported from file: ${file.name}`,
              timestamp: new Date().toISOString()
            }]
          };

          investors.push(investor);
        });

        resolve({ investors, errors });
      } catch (error) {
        resolve({
          investors: [],
          errors: [`Failed to parse file: ${error instanceof Error ? error.message : 'Unknown error'}`]
        });
      }
    };

    reader.readAsArrayBuffer(file);
  });
};
